import classNames from "classnames";
import { BuilderSimpleCartProps } from "../BuilderInterface";
import SimpleCartFooter from "./SimpleCartFooter";
import SimpleCartHeader from "./SimpleCartHeader";
import SimpleCartProduct from "./SimpleCartProduct";
import "./style.scss";
import { NodeStates, SimpleCartNode } from "../../build/types";
import {
    getColorsFromInteractiveStates,
    getCompoundStyle,
    getStateStringForBuilder,
    getStateStyleForBuilder,
    getStylesFromInteractiveStates,
    getStyleStringFromInteractiveState
} from "../helpers";
import { useEffect, useState } from "react";
import DynamicIcon from "../DynamicIcon";
import { useInteractionStates } from "../../hooks/useInteractionStates";
import SimpleCartCustomer from "./SimpleCartCustomer";
import SimpleCartCustomSales from "./SimpleCartCustomSales";

const BuilderSimpleCart: React.FC<BuilderSimpleCartProps> = ({
    node,
    taxIncl,
    calculatedTaxDetails,
    activeCartData,
    actionCallbacks,
    builderProps,
    onProductRemove,
    onCartFeeRemove,
    onEditCustomer,
    onRemoveCustomer,
    onRemoveDiscount,
    dynamicData,
    formatCurrency,
    payments,
    disabled,
    emptyState,
    children,
    onProductSelection,
    onCustomSaleSelection,
    onCustomSaleRemove,
    isCFD
}: BuilderSimpleCartProps) => {
    const cNode = node as SimpleCartNode;
    const { id, attr, expandedNode, stateManager, prepend, append, fallback } = cNode;

    const { products, subtotal, total, tax, discount, customFee, customer, orderNotes, cartTotal, customSales } = activeCartData;
    const [cartOpen, setCartOpen] = useState<boolean>(false);
    const formatC = formatCurrency || ((amount: number) => amount.toFixed(2));

    const { setElement: setButtonElement, states: buttonStates } = useInteractionStates<HTMLSpanElement>();
    const { setElement: setAsideElement, states: asideStates } = useInteractionStates<HTMLElement>();

    const hideCart = () => {
        if (!builderProps) {
            setCartOpen(false);
        }
    };

    useEffect(() => {
        if (typeof builderProps?.active === "boolean") {
            setCartOpen(builderProps.active);
        }
    }, [builderProps?.active]);

    const builderSimpleCartClass = classNames("builder-simple-cart", {
        [`${attr?.className}`]: !!attr?.className
    });

    const asideClass = classNames("expanded-cart", {
        [`${attr?.className}`]: !!attr?.className,
        ["fixed-left"]: expandedNode.openMode === "overlayLeft",
        ["fixed-right"]: expandedNode.openMode === "overlayRight"
    });

    if (builderProps) {
        const { nodeState, selectCallback, outlineCallback, onDragStart, onDragEnd } = builderProps;

        const stateCartWidth = getStateStringForBuilder(stateManager, nodeState, "expandedNode_cartWidth");
        const stateCartBG = getStateStringForBuilder(stateManager, nodeState, "expandedNode_mainBg");
        const stateDividerColor = getStateStringForBuilder(stateManager, nodeState, "expandedNode_dividerColor");
        const stateTotalBG = getStateStringForBuilder(stateManager, nodeState, "expandedNode_totalBg") || expandedNode.totalBg;

        //Icons

        //Open cart icon
        const stateOpenCartIconStyle = getStateStyleForBuilder(stateManager, nodeState, "expandedNode_openCartIcon_style");
        const openCartIcon = expandedNode.openCartIcon?.src ? (
            <DynamicIcon
                name={expandedNode.openCartIcon.src}
                style={getCompoundStyle({
                    otherStyle: expandedNode.openCartIcon.style,
                    stateStyle: stateOpenCartIconStyle
                })}
                colors={expandedNode.openCartIcon.colors}
            />
        ) : (
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={getCompoundStyle({
                    otherStyle: expandedNode.openCartIcon?.style,
                    stateStyle: stateOpenCartIconStyle
                })}
            >
                <path
                    d="M5 9L5.4597 13.597C5.71648 16.1648 5.84487 17.4487 6.70195 18.2244C7.55902 19 8.84932 19 11.4299 19H12.5701C15.1507 19 16.441 19 17.2981 18.2244C18.1551 17.4487 18.2835 16.1648 18.5403 13.597L19 9"
                    stroke="#3D4C66"
                    strokeWidth="1.5"
                />
                <path d="M4 9H20" stroke="#3D4C66" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M9 15V13" stroke="#3D4C66" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M12 15V13" stroke="#3D4C66" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M15 15V13" stroke="#3D4C66" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M6 9L9 5" stroke="#3D4C66" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M18 9L15 5" stroke="#3D4C66" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
        );

        //Cart icon
        const stateCartIconStyle = getStateStyleForBuilder(stateManager, nodeState, "expandedNode_icon_style");
        const cartIcon = expandedNode?.icon?.src ? (
            <DynamicIcon
                name={expandedNode.icon.src}
                style={getCompoundStyle({
                    otherStyle: expandedNode.icon.style,
                    stateStyle: stateCartIconStyle
                })}
                colors={expandedNode.icon.colors}
            />
        ) : (
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={getCompoundStyle({
                    otherStyle: expandedNode.icon?.style,
                    stateStyle: stateCartIconStyle
                })}
            >
                <path
                    d="M5 9L5.4597 13.597C5.71648 16.1648 5.84487 17.4487 6.70195 18.2244C7.55902 19 8.84932 19 11.4299 19H12.5701C15.1507 19 16.441 19 17.2981 18.2244C18.1551 17.4487 18.2835 16.1648 18.5403 13.597L19 9"
                    stroke="#3D4C66"
                    strokeWidth="1.5"
                />
                <path d="M4 9H20" stroke="#3D4C66" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M9 15V13" stroke="#3D4C66" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M12 15V13" stroke="#3D4C66" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M15 15V13" stroke="#3D4C66" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M6 9L9 5" stroke="#3D4C66" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M18 9L15 5" stroke="#3D4C66" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
        );

        //Customer Icons
        const customerIcon = (
            <DynamicIcon
                name={expandedNode?.customerIcon?.src || ""}
                style={getCompoundStyle({
                    otherStyle: expandedNode?.customerIcon?.style,
                    stateStyle: getStateStyleForBuilder(stateManager, nodeState, "expandedNode_customerIcon_style")
                })}
                colors={expandedNode?.customerIcon?.colors}
            />
        );

        const customerXIcon = (
            <DynamicIcon
                name={expandedNode?.customerXIcon?.src || ""}
                style={getCompoundStyle({
                    otherStyle: expandedNode?.customerXIcon?.style,
                    stateStyle: getStateStyleForBuilder(stateManager, nodeState, "expandedNode_customerXIcon_style")
                })}
                colors={expandedNode?.customerXIcon?.colors}
            />
        );

        const fallbackStyle = getCompoundStyle({
            node,
            otherStyle: fallback?.style || {},
            stateStyle: getStateStyleForBuilder(stateManager, nodeState, "fallback_style")
        });

        const prependStyle = getCompoundStyle({
            node,
            otherStyle: prepend?.style || {},
            stateStyle: getStateStyleForBuilder(stateManager, nodeState, "prepend_style")
        });

        const appendStyle = getCompoundStyle({
            node,
            otherStyle: append?.style || {},
            stateStyle: getStateStyleForBuilder(stateManager, nodeState, "append_style")
        });

        return (
            <div
                id={id}
                {...attr}
                className={builderSimpleCartClass}
                onClick={e => (selectCallback ? selectCallback(e, id) : {})}
                onMouseMove={e => (outlineCallback ? outlineCallback(e, id) : {})}
                onDragStart={e => onDragStart?.(e, id)}
                draggable={"true"}
                onDragEnd={onDragEnd}
            >
                <span
                    className="cart-button"
                    style={getCompoundStyle({
                        node,
                        stateStyle: getStateStyleForBuilder(stateManager, nodeState, "node_attr")
                    })}
                >
                    {openCartIcon}
                    {(expandedNode.showTotal || expandedNode.showQuantity) && (
                        <span
                            style={getCompoundStyle({
                                otherStyle: expandedNode.typography?.openCart,
                                stateStyle: getStateStyleForBuilder(stateManager, nodeState, "expandedNode_typography_openCart")
                            })}
                        >
                            {expandedNode.showTotal && `${formatC(total)} `}
                            {expandedNode.showQuantity && products.length > 0 && `(${products.length} Items)`}
                        </span>
                    )}
                </span>
                {cartOpen && (
                    //
                    <aside
                        className={asideClass}
                        style={getCompoundStyle({
                            otherStyle: {
                                ...(expandedNode.cartWidth ? { width: expandedNode.cartWidth } : {}),
                                ...(expandedNode.mainBg ? { backgroundColor: expandedNode.mainBg } : {})
                            },
                            stateStyle: {
                                ...(stateCartWidth ? { width: stateCartWidth } : {}),
                                ...(stateCartBG ? { backgroundColor: stateCartBG } : {})
                            }
                        })}
                    >
                        <SimpleCartHeader
                            dividerBg={stateDividerColor || expandedNode.dividerColor}
                            name={expandedNode.name}
                            titleStyle={getCompoundStyle({
                                otherStyle: expandedNode.typography?.cartName,
                                stateStyle: getStateStyleForBuilder(stateManager, nodeState, "expandedNode_typography_cartName")
                            })}
                            hideCart={hideCart}
                            cartIcon={cartIcon}
                            closeCartIcon={expandedNode.closeIcon}
                            stateManager={stateManager}
                            builder
                            nodeState={nodeState}
                        />
                        <section>
                            {emptyState ? (
                                children
                            ) : (
                                <>
                                    {cNode.expandedNode.showActiveCustomer !== false && customer && (
                                        <SimpleCartCustomer
                                            onClose={() => {}}
                                            onEdit={() => {}}
                                            dynamicData={dynamicData || ""}
                                            customerIcon={customerIcon}
                                            closeCustomerIcon={customerXIcon}
                                            customerTypography={getCompoundStyle({
                                                otherStyle: expandedNode.typography?.customer,
                                                stateStyle: getStateStyleForBuilder(
                                                    stateManager,
                                                    nodeState,
                                                    "expandedNode_typography_customer"
                                                )
                                            })}
                                            customerChipStyle={getCompoundStyle({
                                                otherStyle: expandedNode.customerChip,
                                                stateStyle: getStateStyleForBuilder(
                                                    stateManager,
                                                    nodeState,
                                                    "expandedNode_customerChip"
                                                )
                                            })}
                                            prepend={{ style: prependStyle, value: prepend?.value || "" }}
                                            append={{ style: appendStyle, value: append?.value || "" }}
                                            fallback={{ style: fallbackStyle, value: fallback?.value || "" }}
                                        />
                                    )}
                                    {[...products].reverse().map((p, index) => (
                                        <SimpleCartProduct
                                            key={index}
                                            product={p}
                                            dividerBg={stateDividerColor}
                                            productStyle={getCompoundStyle({
                                                otherStyle: expandedNode.typography?.product,
                                                stateStyle: getStateStyleForBuilder(
                                                    stateManager,
                                                    nodeState,
                                                    "expandedNode_typography_product"
                                                )
                                            })}
                                            productSubtextStyle={getCompoundStyle({
                                                otherStyle: expandedNode.typography?.productSubtext,
                                                stateStyle: getStateStyleForBuilder(
                                                    stateManager,
                                                    nodeState,
                                                    "expandedNode_typography_productSubtext"
                                                )
                                            })}
                                            expandedNode={expandedNode}
                                            formatCurrency={formatC}
                                            stateManager={stateManager}
                                            builder
                                            nodeState={nodeState}
                                        />
                                    ))}

                                    {expandedNode.showOrderNotes && (
                                        <div style={{ display: "flex", flexDirection: "column" }}>
                                            <span
                                                className="cart-order-notes-label"
                                                style={getCompoundStyle({
                                                    otherStyle: expandedNode.typography?.orderNoteLabel,
                                                    stateStyle: getStateStyleForBuilder(
                                                        stateManager,
                                                        nodeState,
                                                        "expandedNode_typography_orderNoteLabel"
                                                    )
                                                })}
                                            >
                                                {expandedNode.showOrderNotesLabel && expandedNode.orderNotesLabel}
                                            </span>
                                            <span
                                                className="cart-order-notes"
                                                style={getCompoundStyle({
                                                    otherStyle: expandedNode.typography?.orderNote,
                                                    stateStyle: getStateStyleForBuilder(
                                                        stateManager,
                                                        nodeState,
                                                        "expandedNode_typography_orderNote"
                                                    )
                                                })}
                                            >
                                                {orderNotes}
                                            </span>
                                        </div>
                                    )}
                                </>
                            )}
                        </section>
                        <SimpleCartFooter
                            tax={tax}
                            total={total}
                            taxIncl={taxIncl}
                            bg={stateTotalBG}
                            discount={discount}
                            subtotal={subtotal}
                            showTaxes={expandedNode.showTaxes}
                            taxesLabel={expandedNode.customizeTaxes}
                            dividerBg={stateDividerColor || expandedNode.dividerColor}
                            totalStyle={getCompoundStyle({
                                otherStyle: expandedNode.typography?.total,
                                stateStyle: getStateStyleForBuilder(stateManager, nodeState, "expandedNode_typography_total")
                            })}
                            subtotalStyle={getCompoundStyle({
                                otherStyle: expandedNode.typography?.subtotals,
                                stateStyle: getStateStyleForBuilder(stateManager, nodeState, "expandedNode_typography_subtotals")
                            })}
                            showDiscount={expandedNode.showCartDiscount}
                            showSubtotal={expandedNode.showCartSubtotal}
                            onRemoveDiscount={onRemoveDiscount}
                            subtotalLabel={expandedNode.customizeCartSubtotal}
                            showTotal={expandedNode.showCartTotal}
                            totalLabel={expandedNode.customizeCartTotal}
                            formatCurrency={formatC}
                            balanceLabel={expandedNode.balanceDueLabel}
                            showBalance={expandedNode.showBalance}
                            payments={payments}
                            cartFees={customFee}
                            calculatedCartFees={calculatedTaxDetails?.cartFees}
                            cartTotal={cartTotal}
                            orderTotalLabel={expandedNode.orderTotalLabel}
                            showOrderTotal={expandedNode.showOrderTotal}
                            summarySubtextStyle={getCompoundStyle({
                                otherStyle: expandedNode?.typography?.summarySubtext,
                                stateStyle: getStateStyleForBuilder(
                                    stateManager,
                                    nodeState,
                                    "expandedNode_typography_summarySubtext"
                                )
                            })}
                        />
                    </aside>
                )}
            </div>
        );
    } else {
        //Button
        const openCartSpanStyle = getStylesFromInteractiveStates({
            states: buttonStates,
            defaultStyle: expandedNode.typography?.openCart,
            stateManager,
            paths: ["expandedNode_typography_openCart"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        let OpenCartIcon = (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M5 9L5.4597 13.597C5.71648 16.1648 5.84487 17.4487 6.70195 18.2244C7.55902 19 8.84932 19 11.4299 19H12.5701C15.1507 19 16.441 19 17.2981 18.2244C18.1551 17.4487 18.2835 16.1648 18.5403 13.597L19 9"
                    stroke="#3D4C66"
                    strokeWidth="1.5"
                />
                <path d="M4 9H20" stroke="#3D4C66" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M9 15V13" stroke="#3D4C66" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M12 15V13" stroke="#3D4C66" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M15 15V13" stroke="#3D4C66" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M6 9L9 5" stroke="#3D4C66" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M18 9L15 5" stroke="#3D4C66" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
        );

        const summarySubtextWithState = getStylesFromInteractiveStates({
            states: asideStates,
            defaultStyle: expandedNode.typography?.summarySubtext,
            stateManager,
            paths: ["expandedNode_typography_summarySubtext"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const customerStyleWithState = getStylesFromInteractiveStates({
            states: asideStates,
            defaultStyle: expandedNode.customerChip,
            stateManager,
            paths: ["expandedNode_customerChip"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const customerTypographyStyleWithState = getStylesFromInteractiveStates({
            states: asideStates,
            defaultStyle: expandedNode.typography?.customer,
            stateManager,
            paths: ["expandedNode_typography_customer"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        //Icons
        let DeleteIcon = (
            <svg style={{ width: "32px", height: "32px" }} viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="0.5" y="0.5" width="17" height="17" rx="3.5" stroke="#ABA7AF" />
                <path
                    d="M5 5L13 13M5 13L13 5"
                    stroke="#ABA7AF"
                    strokeWidth="1.08333"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        );

        let CustomerIcon = (
            <svg
                style={{ width: "32px", height: "32px" }}
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <circle cx="12" cy="9" r="4" stroke="#758696" stroke-width="1.5" />
                <path
                    d="M6 19L6.0883 18.7351C6.52832 17.4151 6.74832 16.755 7.2721 16.3775C7.79587 16 8.49159 16 9.88304 16H14.117C15.5084 16 16.2041 16 16.7279 16.3775C17.2517 16.755 17.4717 17.4151 17.9117 18.7351L18 19"
                    stroke="#758696"
                    stroke-width="1.5"
                    stroke-linecap="round"
                />
            </svg>
        );

        if (expandedNode?.customerXIcon?.src) {
            const style = getStylesFromInteractiveStates({
                states: asideStates,
                defaultStyle: expandedNode.customerXIcon?.style,
                stateManager,
                paths: ["expandedNode_customerXIcon_style"],
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            });

            const colors = getColorsFromInteractiveStates({
                states: asideStates,
                stateManager,
                paths: ["expandedNode_customerXIcon_colors"],
                defaultColors: expandedNode.customerXIcon?.colors,
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            });

            DeleteIcon = (
                <DynamicIcon name={expandedNode.customerXIcon.src} style={{ width: "18px", ...style }} colors={colors} />
            );
        }

        if (expandedNode.openCartIcon?.src) {
            const openCartIconStyle = getStylesFromInteractiveStates({
                states: buttonStates,
                defaultStyle: expandedNode.openCartIcon.style,
                stateManager,
                paths: ["expandedNode_openCartIcon_style"],
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            });

            const openCartIconColors = getColorsFromInteractiveStates({
                states: buttonStates,
                stateManager,
                paths: ["expandedNode_openCartIcon_colors"],
                defaultColors: expandedNode.openCartIcon.colors,
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            });

            OpenCartIcon = (
                <DynamicIcon name={expandedNode.openCartIcon.src} style={openCartIconStyle} colors={openCartIconColors} />
            );
        }

        //Aside
        const cartWidth =
            getStyleStringFromInteractiveState({
                states: asideStates,
                stateManager,
                path: "expandedNode_cartWidth",
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            }) || expandedNode.cartWidth;

        const mainBg =
            getStyleStringFromInteractiveState({
                states: asideStates,
                stateManager,
                path: "expandedNode_mainBg",
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            }) || expandedNode.mainBg;

        const cartHeight =
            getStyleStringFromInteractiveState({
                states: asideStates,
                stateManager,
                path: "expandedNode_cartHeight",
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            }) || expandedNode.cartHeight;

        const dividerColor =
            getStyleStringFromInteractiveState({
                states: asideStates,
                stateManager,
                path: "expandedNode_dividerColor",
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            }) || expandedNode.dividerColor;

        const totalBg =
            getStyleStringFromInteractiveState({
                states: asideStates,
                stateManager,
                path: "expandedNode_totalBg",
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            }) || expandedNode.totalBg;

        const asideStyles = {
            ...(cartWidth ? { width: cartWidth } : {}),
            ...(mainBg ? { backgroundColor: mainBg } : {}),
            ...(cartHeight ? { height: cartHeight } : {})
        };

        const productStyleWithState = getStylesFromInteractiveStates({
            states: asideStates,
            defaultStyle: expandedNode.typography?.product,
            stateManager,
            paths: ["expandedNode_typography_product"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const productSubtextStyleWithState = getStylesFromInteractiveStates({
            states: asideStates,
            defaultStyle: expandedNode.typography?.productSubtext,
            stateManager,
            paths: ["expandedNode_typography_productSubtext"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const subtotalStyleWithState = getStylesFromInteractiveStates({
            states: asideStates,
            defaultStyle: expandedNode.typography?.subtotals,
            stateManager,
            paths: ["expandedNode_typography_subtotals"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const totalStyleWithState = getStylesFromInteractiveStates({
            states: asideStates,
            defaultStyle: expandedNode.typography?.total,
            stateManager,
            paths: ["expandedNode_typography_total"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const fallbackStyle = getStylesFromInteractiveStates({
            states: asideStates,
            defaultStyle: fallback?.style || {},
            stateManager,
            paths: ["fallback_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const prependStyle = getStylesFromInteractiveStates({
            states: asideStates,
            stateManager,
            paths: ["prepend_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const appendStyle = getStylesFromInteractiveStates({
            states: asideStates,
            stateManager,
            paths: ["append_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        return (
            <div id={id} {...attr} className={builderSimpleCartClass} {...actionCallbacks} style={getCompoundStyle({ node })}>
                <span className="cart-button" onClick={() => !disabled && setCartOpen(true)} ref={setButtonElement}>
                    {OpenCartIcon}
                    {(expandedNode.showTotal || expandedNode.showQuantity) && (
                        <span className="cart-button-text" style={openCartSpanStyle}>
                            {expandedNode.showTotal && `${formatC(total)} `}
                            {expandedNode.showQuantity && products.length > 0 && `(${products.length} Items)`}
                        </span>
                    )}
                </span>
                {cartOpen && (
                    <aside className={asideClass} ref={setAsideElement} style={asideStyles}>
                        <SimpleCartHeader
                            dividerBg={dividerColor}
                            name={expandedNode.name}
                            hideCart={() => setCartOpen(false)}
                            cartIcon={OpenCartIcon}
                            closeCartIcon={expandedNode.closeIcon}
                            titleStyle={expandedNode.typography?.cartName}
                            disabled={disabled}
                        />
                        <section>
                            {cNode.expandedNode.showActiveCustomer !== false && customer && (
                                <SimpleCartCustomer
                                    onClose={onRemoveCustomer}
                                    onEdit={onEditCustomer}
                                    dynamicData={dynamicData || ""}
                                    customerIcon={CustomerIcon}
                                    closeCustomerIcon={DeleteIcon}
                                    customerTypography={customerTypographyStyleWithState}
                                    customerChipStyle={customerStyleWithState}
                                    prepend={{ style: prependStyle, value: prepend?.value || "" }}
                                    append={{ style: appendStyle, value: append?.value || "" }}
                                    fallback={{ style: fallbackStyle, value: fallback?.value || "" }}
                                />
                            )}
                            {!customFee && (!products || products.length === 0) && (!customSales || customSales.length === 0) ? (
                                children
                            ) : (
                                <>
                                    {[...products].reverse().map((p, index) => {
                                        const taxDetails = calculatedTaxDetails?.products[p.internalId || p.id];
                                        // Line item shows only product (item) discount; cart discount is shown once in footer
                                        const calculatedAmounts =
                                            taxDetails != null
                                                ? {
                                                      discountAmount: (taxDetails.prodDiscount ?? 0) * p.quantity,
                                                      feeAmount: taxDetails.fee ?? 0,
                                                      discountLabel:
                                                          taxDetails.prodDiscountLabel ?? p.discount?.label,
                                                      feeLabel: taxDetails.feeLabel ?? p.fee?.label
                                                  }
                                                : undefined;
                                        return (
                                            <SimpleCartProduct
                                                key={index}
                                                product={p}
                                                calculatedAmounts={calculatedAmounts}
                                                onRemove={() => {
                                                    onProductRemove && onProductRemove(p.internalId);
                                                }}
                                                onSelect={() => {
                                                    onProductSelection && onProductSelection(p);
                                                }}
                                                dividerBg={dividerColor}
                                                productStyle={productStyleWithState}
                                                productSubtextStyle={productSubtextStyleWithState}
                                                formatCurrency={formatC}
                                                stateManager={stateManager}
                                                disabled={disabled}
                                                isCFD={isCFD}
                                                expandedNode={expandedNode}
                                            />
                                        );
                                    })}

                                    {customSales?.map((sale, index) => (
                                        <SimpleCartCustomSales
                                            key={index}
                                            customSale={sale}
                                            onSelect={() => {
                                                onCustomSaleSelection && onCustomSaleSelection(sale);
                                            }}
                                            onRemove={() => {
                                                onCustomSaleRemove && onCustomSaleRemove(sale.id);
                                            }}
                                            dividerBg={dividerColor}
                                            productStyle={productStyleWithState}
                                            productSubtextStyle={productSubtextStyleWithState}
                                            formatCurrency={formatC}
                                            stateManager={stateManager}
                                            disabled={disabled}
                                            isCFD={isCFD}
                                            expandedNode={expandedNode}
                                        />
                                    ))}

                                    {expandedNode.showOrderNotes && orderNotes && (
                                        <div style={{ display: "flex", flexDirection: "column" }}>
                                            <span className="cart-order-notes-label">
                                                {expandedNode.showOrderNotesLabel && expandedNode.orderNotesLabel}
                                            </span>
                                            <span className="cart-order-notes">{orderNotes}</span>
                                        </div>
                                    )}
                                </>
                            )}
                        </section>
                        <SimpleCartFooter
                            tax={tax}
                            total={total}
                            taxIncl={taxIncl}
                            bg={totalBg}
                            discount={discount}
                            subtotal={subtotal}
                            showTaxes={expandedNode.showTaxes}
                            dividerBg={dividerColor}
                            totalStyle={totalStyleWithState}
                            subtotalStyle={subtotalStyleWithState}
                            taxesLabel={expandedNode.customizeTaxes}
                            showDiscount={expandedNode.showCartDiscount}
                            showSubtotal={expandedNode.showCartSubtotal}
                            subtotalLabel={expandedNode.customizeCartSubtotal}
                            showTotal={expandedNode.showCartTotal}
                            totalLabel={expandedNode.customizeCartTotal}
                            balanceLabel={expandedNode.balanceDueLabel}
                            showBalance={expandedNode.showBalance}
                            formatCurrency={formatC}
                            onRemoveDiscount={onRemoveDiscount}
                            payments={payments}
                            cartFees={customFee}
                            calculatedCartFees={calculatedTaxDetails?.cartFees}
                            cartTotal={cartTotal}
                            orderTotalLabel={expandedNode.orderTotalLabel}
                            showOrderTotal={expandedNode.showOrderTotal}
                            summarySubtextStyle={summarySubtextWithState}
                            onCartFeeRemove={onCartFeeRemove}
                        />
                    </aside>
                )}
            </div>
        );
    }
};

export default BuilderSimpleCart;
